import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ZoomIn, ShoppingBag, Check, Mail } from "lucide-react";
import { FadeIn } from "@/components/common/FadeIn";
import { Lightbox } from "@/components/common/Lightbox";
import { NotFound } from "@/components/common/NotFound";
import { InquireModal } from "@/components/common/InquireModal";
import { useCart } from "@/lib/cart";
import { PURCHASING_ENABLED } from "@/lib/purchasing";
import api from "@/lib/api";

export default function ArtworkDetail() {
  const { id } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [inquireOpen, setInquireOpen] = useState(false);
  const { addItem, isInCart } = useCart();

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    api.get(`/artworks/${id}`)
      .then((r) => {
        if (cancelled) return;
        setArtwork(r.data);
        setLoadState("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadState(err?.response?.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
  }, [id]);

  if (loadState === "not_found" || loadState === "error") {
    return (
      <NotFound
        subject="artwork"
        backTo="/artists"
        backLabel="All Artists"
        isError={loadState === "error"}
      />
    );
  }

  if (loadState === "loading" || !artwork) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="artwork-detail-page" className="pt-20">
      <section className="py-12 md:py-20 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <Link
            to={`/artists/${artwork.artist_slug}`}
            className="inline-flex items-center gap-2 text-sm text-[#8A94A6] hover:text-[#0A1128] transition-colors mb-8"
            data-testid="back-to-artist"
          >
            <ArrowLeft size={14} /> Back to {artwork.artist_name}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 lg:gap-16">
            {/* Image */}
            <FadeIn>
              <div
                className="relative aspect-[4/5] overflow-hidden bg-[#F8F9FA] cursor-pointer group"
                onClick={() => setLightboxOpen(true)}
                data-testid="artwork-image-container"
              >
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white">
                    <ZoomIn size={32} />
                  </span>
                </div>
              </div>
            </FadeIn>

            {/* Info */}
            <FadeIn delay={0.15}>
              <div className="lg:sticky lg:top-28">
                <p className="overline mb-3">
                  <Link
                    to={`/artists/${artwork.artist_slug}`}
                    className="hover:text-[#0A1128] transition-colors"
                    data-testid="artwork-artist-link"
                  >
                    {artwork.artist_name}
                  </Link>
                </p>
                <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A1128] mb-6">
                  {artwork.title}
                </h1>

                <div className="border-t border-[#0A1128]/10 pt-6 space-y-4 mb-8">
                  {artwork.description && (
                    <p className="text-[#4A5568] text-sm leading-relaxed">{artwork.description}</p>
                  )}
                  {artwork.dimensions && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#8A94A6]">Dimensions</span>
                      <span className="text-[#0A1128]">{artwork.dimensions}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-[#8A94A6]">Medium</span>
                    <span className="text-[#0A1128]">{artwork.artist_name}</span>
                  </div>
                </div>

                {/* Price & Purchase */}
                <div className="border-t border-[#0A1128]/10 pt-6">
                  {artwork.sold ? (
                    <div
                      className="w-full py-4 text-center bg-[#F8F9FA] text-[#8A94A6] text-xs uppercase tracking-[0.2em]"
                      data-testid="sold-indicator"
                    >
                      Sold
                    </div>
                  ) : (
                    <>
                      <p className="font-heading text-2xl text-[#0A1128] mb-4" data-testid="artwork-price">
                        ${artwork.price?.toLocaleString()}
                      </p>
                      {isInCart(artwork.id) && PURCHASING_ENABLED ? (
                        <div
                          className="w-full py-4 text-center bg-[#F8F9FA] text-[#0A1128] text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                          data-testid="in-cart-indicator"
                        >
                          <Check size={14} /> Added to Cart
                        </div>
                      ) : PURCHASING_ENABLED ? (
                        <button
                          className="btn-fill-hover btn-fill-dark w-full bg-[#0A1128] text-white py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                          data-testid="add-to-cart-button"
                          onClick={() => addItem(artwork)}
                        >
                          <ShoppingBag size={14} /> Add to Cart
                        </button>
                      ) : (
                        <button
                          className="inquire-sweep relative overflow-hidden w-full bg-[#0A1128] text-white flex items-center justify-center gap-2"
                          style={{
                            fontFamily: "'Outfit', sans-serif",
                            fontWeight: 500,
                            fontSize: "0.72rem",
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                            padding: "1rem 1.5rem",
                            transition: "color 0.4s ease",
                          }}
                          data-testid="inquire-button"
                          onClick={() => setInquireOpen(true)}
                        >
                          <span
                            aria-hidden="true"
                            className="inquire-sweep__fill absolute inset-0"
                            style={{ backgroundColor: "#CBA365" }}
                          />
                          <span className="relative z-10 flex items-center gap-2">
                            <Mail size={14} /> Inquire
                          </span>
                        </button>
                      )}
                      <p className="text-[10px] text-[#8A94A6] text-center mt-3">
                        Contact us for shipping details and payment options
                      </p>
                    </>
                  )}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={artwork.image}
        title={artwork.title}
      />
      <InquireModal
        isOpen={inquireOpen}
        onClose={() => setInquireOpen(false)}
        pieceName={artwork.title}
        artistName={artwork.artist_name}
      />
    </div>
  );
}
